import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import DialogflowService, {
  dialogflowService,
  ChatMessage,
} from "../../services/dialogflowService";
import { knowledgeTreeService } from "../../services/knowledgeTreeService";
import { messageService, UserInquiry } from "../../services/messageService";
import { getEnvironmentConfig } from "../../utils/environment";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { chatRateLimiter } from "../../services/rateLimiter";
import { inputValidator } from "../../services/inputValidator";
import { monitoringService } from "../../services/monitoringService";

const ContactContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 1rem 0.5rem;
    max-width: 100vw;
    overflow-x: hidden;
  }

  @media (max-width: 480px) {
    padding: 0.5rem 0.25rem;
    max-width: 100vw;
    overflow-x: hidden;
  }
`;

const ChatSection = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 1rem 1rem;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 0.5rem 0.5rem;
    max-width: 100vw;
    overflow-x: hidden;
  }

  @media (max-width: 480px) {
    padding: 0.25rem 0.25rem;
    max-width: 100vw;
    overflow-x: hidden;
  }
`;

const ChatContainer = styled.div`
  background: var(--card-background);
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  border: 1px solid var(--border-color);
  overflow: hidden;
  height: 700px;
  display: flex;
  flex-direction: column;
  backdrop-filter: blur(10px);
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    height: calc(100vh - 4rem);
    border-radius: 15px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    max-width: 100%;
  }

  @media (max-width: 480px) {
    height: calc(100vh - 2rem);
    border-radius: 12px;
    max-width: 100%;
  }
`;

const ChatHeader = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 1.5rem 2rem;
  font-weight: 600;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 1rem 1.5rem;
    font-size: 1.1rem;
  }

  @media (max-width: 480px) {
    padding: 0.75rem 1rem;
    font-size: 1rem;
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      45deg,
      transparent 30%,
      rgba(255, 255, 255, 0.1) 50%,
      transparent 70%
    );
    animation: shimmer 3s ease-in-out infinite;
  }

  @keyframes shimmer {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }
`;

const EagleIcon = styled.div<{ $isFlying?: boolean }>`
  font-size: 2rem;
  animation: ${(props) =>
    props.$isFlying
      ? "eagleFly 1.5s ease-in-out"
      : "eagleFloat 2s ease-in-out infinite"};
  cursor: pointer;
  transition: all 0.3s ease;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
  user-select: none;

  &:hover {
    transform: scale(1.1) rotate(5deg);
    filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3));
  }

  @keyframes eagleFloat {
    0%,
    100% {
      transform: translateY(0px) rotate(0deg);
    }
    50% {
      transform: translateY(-3px) rotate(2deg);
    }
  }

  @keyframes eagleFly {
    0% {
      transform: translateY(0px) rotate(0deg) scale(1);
    }
    25% {
      transform: translateY(-15px) rotate(-10deg) scale(1.2);
    }
    50% {
      transform: translateY(-25px) rotate(10deg) scale(1.3);
    }
    75% {
      transform: translateY(-15px) rotate(-5deg) scale(1.1);
    }
    100% {
      transform: translateY(0px) rotate(0deg) scale(1);
    }
  }
`;

const ChatMessages = styled.div`
  flex: 1;
  padding: 1rem;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch; /* iOS smooth scrolling */
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 0.75rem;
    gap: 0.75rem;
  }

  @media (max-width: 480px) {
    padding: 0.5rem;
    gap: 0.5rem;
    /* Ensure proper scrolling on mobile */
    overscroll-behavior: contain;
  }
`;

const Message = styled.div<{ $isUser: boolean }>`
  display: flex;
  justify-content: ${(props) => (props.$isUser ? "flex-end" : "flex-start")};
  margin-bottom: 1.5rem;
  animation: messageSlideIn 0.3s ease-out;

  @media (max-width: 768px) {
    margin-bottom: 1rem;
  }

  @media (max-width: 480px) {
    margin-bottom: 0.75rem;
  }

  @keyframes messageSlideIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const MessageBubble = styled.div<{ $isUser: boolean }>`
  max-width: 75%;
  padding: 1.2rem 1.5rem;
  border-radius: ${(props) =>
    props.$isUser ? "20px 20px 5px 20px" : "20px 20px 20px 5px"};
  background: ${(props) =>
    props.$isUser
      ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
      : "var(--surface-color)"};
  color: ${(props) => (props.$isUser ? "white" : "var(--text-color)")};
  box-shadow: ${(props) =>
    props.$isUser
      ? "0 4px 15px rgba(102, 126, 234, 0.3)"
      : "0 2px 10px rgba(0, 0, 0, 0.1)"};
  word-wrap: break-word;
  word-break: break-word;
  white-space: pre-wrap;
  line-height: 1.5;
  position: relative;
  transition: all 0.2s ease;
  border: ${(props) =>
    props.$isUser ? "none" : "1px solid var(--border-color)"};
  box-sizing: border-box;
  overflow-wrap: break-word;

  @media (max-width: 768px) {
    max-width: 85%;
    padding: 1rem 1.25rem;
    border-radius: ${(props) =>
      props.$isUser ? "18px 18px 4px 18px" : "18px 18px 18px 4px"};
  }

  @media (max-width: 480px) {
    max-width: 90%;
    padding: 0.875rem 1rem;
    border-radius: ${(props) =>
      props.$isUser ? "16px 16px 4px 16px" : "16px 16px 16px 4px"};
    font-size: 0.95rem;
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${(props) =>
      props.$isUser
        ? "0 6px 20px rgba(102, 126, 234, 0.4)"
        : "0 4px 15px rgba(0, 0, 0, 0.15)"};
  }
`;

const TypingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-secondary);
  font-style: italic;
  padding: 1rem;
`;

const TypingDots = styled.div`
  display: flex;
  gap: 0.25rem;

  div {
    width: 6px;
    height: 6px;
    background: var(--primary-color);
    border-radius: 50%;
    animation: typing 1.4s infinite ease-in-out;

    &:nth-child(1) {
      animation-delay: -0.32s;
    }

    &:nth-child(2) {
      animation-delay: -0.16s;
    }
  }

  @keyframes typing {
    0%,
    80%,
    100% {
      transform: scale(0);
    }
    40% {
      transform: scale(1);
    }
  }
`;

const InputContainer = styled.div`
  padding: 1.5rem 2rem;
  background: var(--card-background);
  border-top: 1px solid var(--border-color);
  display: flex;
  gap: 1rem;
  align-items: center;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 1rem 1.5rem;
    gap: 0.75rem;
  }

  @media (max-width: 480px) {
    padding: 0.75rem 1rem;
    gap: 0.5rem;
    flex-direction: column;
    align-items: stretch;
  }
`;

const MessageInput = styled.textarea`
  flex: 1;
  padding: 1rem 1.5rem;
  border: 2px solid var(--border-color);
  border-radius: 25px;
  background: var(--background);
  color: var(--text-color);
  font-size: 1rem;
  outline: none;
  transition: all 0.3s ease;
  resize: none;
  min-height: 50px;
  max-height: 200px;
  font-family: inherit;
  line-height: 1.5;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

  @media (max-width: 768px) {
    padding: 0.875rem 1.25rem;
    font-size: 0.95rem;
    border-radius: 20px;
    min-height: 45px;
  }

  @media (max-width: 480px) {
    padding: 0.75rem 1rem;
    font-size: 0.9rem;
    border-radius: 18px;
    min-height: 40px;
    max-height: 150px;
  }

  &:focus {
    border-color: var(--primary-color);
    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.2);
    transform: translateY(-1px);
  }

  &:hover:not(:focus) {
    border-color: var(--primary-color);
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SendButton = styled.button`
  padding: 1rem 1.5rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 0.875rem 1.25rem;
    border-radius: 20px;
    font-size: 0.95rem;
  }

  @media (max-width: 480px) {
    padding: 0.75rem 1rem;
    border-radius: 18px;
    font-size: 0.9rem;
    justify-content: center;
    min-height: 44px; /* Touch target size */
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: left 0.5s;
  }

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);

    &::before {
      left: 100%;
    }
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const QuickActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin: 1rem 1.5rem 1rem 1.5rem;
  padding: 0.5rem 0;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;

  @media (max-width: 768px) {
    margin: 0.75rem 1rem;
    gap: 0.5rem;
    max-width: 100%;
  }

  @media (max-width: 480px) {
    margin: 0.5rem 0.75rem;
    gap: 0.375rem;
    max-width: 100%;
  }
`;

const QuickActionButton = styled.button<{ $isJoinButton?: boolean }>`
  padding: 0.75rem 1.25rem;
  background: ${(props) =>
    props.$isJoinButton
      ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
      : "var(--background)"};
  border: ${(props) =>
    props.$isJoinButton ? "none" : "1px solid var(--border-color)"};
  border-radius: 25px;
  color: ${(props) => (props.$isJoinButton ? "white" : "var(--text-color)")};
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  box-shadow: ${(props) =>
    props.$isJoinButton
      ? "0 4px 15px rgba(102, 126, 234, 0.3)"
      : "0 2px 8px rgba(0, 0, 0, 0.05)"};
  position: relative;
  overflow: hidden;
  outline: none;

  @media (max-width: 768px) {
    padding: 0.625rem 1rem;
    font-size: 0.85rem;
    border-radius: 20px;
  }

  @media (max-width: 480px) {
    padding: 0.5rem 0.875rem;
    font-size: 0.8rem;
    border-radius: 18px;
    min-height: 40px; /* Touch target size */
    max-width: 100%;
    word-wrap: break-word;
    white-space: normal;
  }

  &:focus {
    outline: none;
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, ${(props) => (props.$isJoinButton ? "0.2" : "0.1")}),
      transparent
    );
    transition: left 0.5s;
  }

  &:hover {
    background: ${(props) =>
      props.$isJoinButton
        ? "linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)"
        : "var(--primary-color)"};
    color: white;
    border-color: ${(props) =>
      props.$isJoinButton ? "transparent" : "var(--primary-color)"};
    transform: translateY(-2px);
    box-shadow: ${(props) =>
      props.$isJoinButton
        ? "0 6px 20px rgba(102, 126, 234, 0.4)"
        : "0 4px 15px rgba(102, 126, 234, 0.3)"};

    &::before {
      left: 100%;
    }
  }
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fca5a5;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  margin: 0.5rem 0;
  color: #dc2626;
  font-size: 0.9rem;
  text-align: center;
`;

// const WarningMessage = styled.div`
//   background: #fffbeb;
//   border: 1px solid #fbbf24;
//   border-radius: 8px;
//   padding: 0.75rem 1rem;
//   margin: 0.5rem 0;
//   color: #d97706;
//   font-size: 0.9rem;
//   text-align: center;
// `;

const ContactPage: React.FC = () => {
  const { user, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [conversationStarted, setConversationStarted] = useState(false);
  const [conversationEnded, setConversationEnded] = useState(false);
  const [currentInquiry, setCurrentInquiry] = useState<{
    messages: string[];
    responses: string[];
    intent: string;
    confidence: number;
    isDialogflowResponse: boolean;
  } | null>(null);
  const [inquirySubmitted, setInquirySubmitted] = useState<boolean>(false);
  const [rateLimitError, setRateLimitError] = useState<string>("");
  const [validationError, setValidationError] = useState<string>("");
  const [dynamicQuickActions, setDynamicQuickActions] = useState<string[]>([]);
  const [isEagleFlying, setIsEagleFlying] = useState<boolean>(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const config = getEnvironmentConfig();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Handle mobile keyboard behavior
  useEffect(() => {
    const handleResize = () => {
      // Small delay to ensure layout has updated
      setTimeout(scrollToBottom, 100);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleLogin = async () => {
    try {
      await signInWithGoogle();
      // After successful Google auth, redirect to dashboard
      navigate("/dashboard");
    } catch (error) {
      console.error("Google sign-in failed:", error);
      // If Google auth fails, fall back to login page
      navigate("/login");
    }
  };

  const handleEagleClick = () => {
    setIsEagleFlying(true);
    // Reset the flying state after animation completes
    setTimeout(() => {
      setIsEagleFlying(false);
    }, 1500);
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Initialize session ID and greeting message on component mount
  useEffect(() => {
    const newSessionId = dialogflowService.generateSessionId();
    setSessionId(newSessionId);

    // Reset conversation context to ensure clean start
    knowledgeTreeService.resetContext();

    // Set initial greeting based on user authentication status
    if (user) {
      // Logged in users get full greeting
      setMessages([
        {
          id: "1",
          text: "Soar! 🦅 I'm the NewME Eagle, guardian of self-determination and collaborative mapping. Need help with the platform, have ideas for new features, or want to discuss Middle Eastern state redesign? I'm here to guide you.",
          isUser: false,
          timestamp: new Date(),
        },
      ]);
    } else {
      // Anonymous users get brief greeting with login requirement
      setMessages([
        {
          id: "1",
          text: "Welcome! 🦅 I'm the NewME Eagle assistant. I can help with platform questions, feature requests, and Middle Eastern state redesign discussions. To start chatting, use the quick actions below:",
          isUser: false,
          timestamp: new Date(),
        },
      ]);
    }
  }, [user]);

  // const validateEmail = (email: string): boolean => {
  //   const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  //   return emailRegex.test(email);
  // };

  const quickActions = [
    "Report a bug",
    "Request a feature",
    "General question",
  ];

  const anonymousQuickActions = [
    "Sign In",
    "Report a bug",
    "Request a feature",
    "General question",
  ];

  const handleQuickAction = (action: string) => {
    // Handle "Sign In" action for anonymous users
    if (!user && action === "Sign In") {
      handleLogin();
      return;
    }

    // For anonymous users, show login prompt instead of processing other actions
    if (!user) {
      const loginPromptMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        text: `I'd be happy to help with "${action}"! Please join our community to start chatting.`,
        isUser: false,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, loginPromptMessage]);
      return;
    }

    // For logged in users, process the action normally
    handleSendMessage(action);
  };

  const saveConversationToDatabase = async () => {
    if (!currentInquiry) return;

    try {
      const inquiry: Omit<UserInquiry, "id" | "timestamp"> = {
        userId: user?.id, // Only logged-in users can save conversations
        userEmail: user?.email, // Use user's email from auth
        message: currentInquiry.messages.join(" | "), // Combine all messages
        response: currentInquiry.responses.join(" | "), // Combine all responses
        intent: currentInquiry.intent,
        confidence: currentInquiry.confidence,
        isDialogflowResponse: currentInquiry.isDialogflowResponse,
        sessionId,
        status: "pending",
        userAgent: navigator.userAgent,
        tags: [], // Can be populated based on intent or content analysis
      };

      await messageService.saveInquiry(inquiry);
    } catch (saveError) {
      console.error("Failed to save conversation:", saveError);
    }
  };

  const handleSendMessage = async (messageText: string) => {
    if (!messageText.trim()) return;

    // For anonymous users, show login prompt instead of processing the message
    if (!user) {
      const loginPromptMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        text: "I'd be happy to help! Please use the 'Join Now' button above to start chatting.",
        isUser: false,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, loginPromptMessage]);
      return;
    }

    // Clear previous errors and dynamic quick actions
    setError("");
    setRateLimitError("");
    setValidationError("");
    setDynamicQuickActions([]); // Clear dynamic quick actions when user sends a message

    // Input validation
    const validationResult = inputValidator.validateMessage(messageText);
    if (!validationResult.isValid) {
      setValidationError(inputValidator.getErrorMessage(validationResult));
      return;
    }

    // Check for spam
    const spamCheck = inputValidator.isSpam(messageText);
    if (spamCheck.isSpam) {
      setError(
        "Your message appears to be spam. Please provide a more detailed, relevant message."
      );

      // Record spam activity
      monitoringService.recordActivity({
        type: "spam_detected",
        severity: "medium",
        sessionId,
        userAgent: navigator.userAgent,
        details: `Spam detected: ${spamCheck.reasons.join(", ")}`,
        metadata: {
          confidence: spamCheck.confidence,
          reasons: spamCheck.reasons,
        },
      });
      return;
    }

    // Rate limiting for anonymous users
    if (!user) {
      const rateLimitResult = chatRateLimiter.isAllowed(sessionId);
      if (!rateLimitResult.allowed) {
        const resetTime = new Date(
          rateLimitResult.resetTime
        ).toLocaleTimeString();
        setRateLimitError(
          `Too many messages. Please wait until ${resetTime} before sending another message.`
        );

        // Record rate limit activity
        monitoringService.recordActivity({
          type: "rate_limit_exceeded",
          severity: "high",
          sessionId,
          userAgent: navigator.userAgent,
          details: `Rate limit exceeded for session ${sessionId}`,
          metadata: { resetTime: rateLimitResult.resetTime },
        });
        return;
      }
    }

    // Use sanitized input
    const sanitizedMessage = validationResult.sanitizedInput;

    if (conversationEnded) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      text: sanitizedMessage,
      isUser: true,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setIsTyping(true);
    setError("");

    // Check for conversation end signals using the knowledge tree service
    const isEndingMessage =
      knowledgeTreeService.isConversationEnding(sanitizedMessage);

    try {
      let aiResponse: string;
      let intent: string = "Unknown";
      let confidence: number = 0;
      let isDialogflowResponse = false;

      // Check if Dialogflow is enabled and configured
      if (
        config.dialogflow.enabled &&
        config.dialogflow.projectId &&
        config.dialogflow.accessToken
      ) {
        try {
          // Create a new service instance with the current session and config
          const service = new DialogflowService({
            projectId: config.dialogflow.projectId,
            sessionId: sessionId,
            accessToken: config.dialogflow.accessToken,
            languageCode: "en",
          });

          const response = await service.sendMessage(sanitizedMessage);
          const processedResponse = service.processResponse(response);

          aiResponse = processedResponse.text;
          intent = processedResponse.intent;
          confidence = processedResponse.confidence;
          isDialogflowResponse = true;
        } catch (dialogflowError) {
          // Fall back to knowledge tree if Dialogflow fails
          if (config.dialogflow.fallbackEnabled) {
            const fallbackResponse =
              dialogflowService.generateFallbackResponse(sanitizedMessage);
            aiResponse = fallbackResponse.response;
            // Store follow-up questions as dynamic quick actions
            if (fallbackResponse.followUpQuestions) {
              setDynamicQuickActions(fallbackResponse.followUpQuestions);
            }
          } else {
            throw dialogflowError;
          }
        }
      } else {
        // Use knowledge tree when Dialogflow is not configured
        const fallbackResponse =
          dialogflowService.generateFallbackResponse(sanitizedMessage);
        aiResponse = fallbackResponse.response;
        // Store follow-up questions as dynamic quick actions
        if (fallbackResponse.followUpQuestions) {
          setDynamicQuickActions(fallbackResponse.followUpQuestions);
        }
      }

      // Add a small delay to make the interaction feel more natural
      await new Promise((resolve) =>
        setTimeout(resolve, 800 + Math.random() * 1200)
      );

      const aiMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        text: aiResponse,
        isUser: false,
        timestamp: new Date(),
        intent,
        confidence,
      };

      setMessages((prev) => [...prev, aiMessage]);

      // Focus the input field after AI responds
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);

      // Update current inquiry with new message
      if (!conversationStarted) {
        setConversationStarted(true);
        setCurrentInquiry({
          messages: [sanitizedMessage],
          responses: [aiResponse],
          intent,
          confidence,
          isDialogflowResponse,
        });
      } else if (currentInquiry) {
        setCurrentInquiry({
          ...currentInquiry,
          messages: [...currentInquiry.messages, sanitizedMessage],
          responses: [...currentInquiry.responses, aiResponse],
          intent, // Update to latest intent
          confidence, // Update to latest confidence
          isDialogflowResponse, // Update to latest source
        });
      }

      // Handle conversation end
      if (isEndingMessage) {
        setConversationEnded(true);
        setInputValue(""); // Clear input

        // Only save for logged-in users
        if (user && currentInquiry) {
          await saveConversationToDatabase();
        }
      }
    } catch (error) {
      console.error("Error getting AI response:", error);
      setError("Sorry, I encountered an error. Please try again.");

      const errorMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        text: "I'm sorry, but I'm having trouble processing your request right now. Please try again in a moment.",
        isUser: false,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSendMessage(inputValue);
  };

  const startNewConversation = () => {
    // Set greeting based on user authentication status
    if (user) {
      setMessages([
        {
          id: "1",
          text: "Soar! 🦅 I'm the NewME Eagle, guardian of self-determination and collaborative mapping. Need help with the platform, have ideas for new features, or want to discuss Middle Eastern state redesign? I'm here to guide you.",
          isUser: false,
          timestamp: new Date(),
        },
      ]);
    } else {
      setMessages([
        {
          id: "1",
          text: "Welcome! 🦅 I'm the NewME Eagle assistant. I can help with platform questions, feature requests, and Middle Eastern state redesign discussions. To start chatting, use the quick actions below:",
          isUser: false,
          timestamp: new Date(),
        },
      ]);
    }

    setConversationStarted(false);
    setConversationEnded(false);
    setCurrentInquiry(null);
    setError("");
    setDynamicQuickActions([]); // Clear dynamic quick actions
    setInquirySubmitted(false); // Reset inquiry submitted state
    const newSessionId = dialogflowService.generateSessionId();
    setSessionId(newSessionId);
  };

  return (
    <div
      style={{
        width: "100%",
        boxSizing: "border-box",
        position: "relative",
      }}
    >
      <ContactContainer>
        <ChatSection>
          <ChatContainer>
            <ChatHeader>
              <EagleIcon $isFlying={isEagleFlying} onClick={handleEagleClick}>
                🦅
              </EagleIcon>
              <span>NewME Eagle</span>
            </ChatHeader>

            <ChatMessages>
              {messages.map((message) => (
                <Message key={message.id} $isUser={message.isUser}>
                  <MessageBubble $isUser={message.isUser}>
                    {message.text}
                  </MessageBubble>
                </Message>
              ))}

              {isTyping && (
                <TypingIndicator>
                  <span>NewME Eagle is thinking</span>
                  <TypingDots>
                    <div></div>
                    <div></div>
                    <div></div>
                  </TypingDots>
                </TypingIndicator>
              )}

              {conversationEnded && user && (
                <div style={{ textAlign: "center", margin: "1rem 0" }}>
                  <button
                    onClick={startNewConversation}
                    style={{
                      padding: "0.75rem 1.5rem",
                      background: "var(--primary-color)",
                      color: "white",
                      border: "none",
                      borderRadius: "25px",
                      cursor: "pointer",
                      fontSize: "0.9rem",
                      fontWeight: "600",
                      boxShadow: "0 4px 15px rgba(0, 123, 255, 0.3)",
                      transition: "all 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow =
                        "0 6px 20px rgba(0, 123, 255, 0.4)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow =
                        "0 4px 15px rgba(0, 123, 255, 0.3)";
                    }}
                  >
                    Start New Conversation
                  </button>
                </div>
              )}

              <div ref={messagesEndRef} />
            </ChatMessages>

            {!conversationEnded && !inquirySubmitted && (
              <>
                {/* Show quick actions when available - dynamic from knowledge tree or static for first message */}
                {(dynamicQuickActions.length > 0 || !conversationStarted) && (
                  <QuickActions>
                    {(dynamicQuickActions.length > 0
                      ? dynamicQuickActions
                      : user
                      ? quickActions
                      : anonymousQuickActions
                    ).map((action) => (
                      <QuickActionButton
                        key={action}
                        onClick={() => handleQuickAction(action)}
                        disabled={isTyping}
                        $isJoinButton={action === "Sign In"}
                      >
                        {action}
                      </QuickActionButton>
                    ))}
                  </QuickActions>
                )}

                <InputContainer>
                  <form
                    onSubmit={handleSubmit}
                    style={{
                      display: "flex",
                      gap: "1rem",
                      width: "100%",
                      maxWidth: "100%",
                      boxSizing: "border-box",
                      overflow: "hidden",
                    }}
                  >
                    <MessageInput
                      ref={inputRef}
                      value={inputValue}
                      onChange={(e) => {
                        setInputValue(e.target.value);
                        // Auto-resize textarea
                        e.target.style.height = "auto";
                        e.target.style.height =
                          Math.min(e.target.scrollHeight, 200) + "px";
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          if (inputValue.trim() && !isTyping) {
                            handleSendMessage(inputValue);
                          }
                        }
                      }}
                      onFocus={() => {
                        // Scroll to bottom when input is focused on mobile
                        setTimeout(scrollToBottom, 300);
                      }}
                      placeholder={
                        user
                          ? "Type your message here..."
                          : "Please log in to start chatting..."
                      }
                      disabled={isTyping || !user}
                      rows={1}
                    />
                    <SendButton
                      type="submit"
                      disabled={isTyping || !inputValue.trim() || !user}
                    >
                      Send
                    </SendButton>
                  </form>
                </InputContainer>
              </>
            )}

            {error && <ErrorMessage>{error}</ErrorMessage>}
            {rateLimitError && <ErrorMessage>{rateLimitError}</ErrorMessage>}
            {validationError && <ErrorMessage>{validationError}</ErrorMessage>}
          </ChatContainer>
        </ChatSection>
      </ContactContainer>
    </div>
  );
};

export default ContactPage;
